import { useEffect, useState } from 'react';
import { useScrollReveal } from '@/hooks/useScrollReveal';

interface ProjectHeroProps {
  project: {
    title: string;
    category: string;
    status: string;
    location: string;
    description: string;
    image: string;
    plotSizes: string[];
    priceFrom: string;
  };
}

const WHATSAPP_NUMBER = '+923348872555';
const WHATSAPP_URL = `https://wa.me/${WHATSAPP_NUMBER.replace('+', '')}`;

export default function ProjectHero({ project }: ProjectHeroProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const { ref, isVisible } = useScrollReveal({ threshold: 0 });

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section ref={ref} className="relative w-full min-h-[85vh] md:min-h-[90vh] flex items-end overflow-hidden">
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={project.image}
          alt={project.title}
          className={`w-full h-full object-cover object-top transition-transform duration-[1200ms] ease-out ${isLoaded ? 'scale-100' : 'scale-110'}`}
        />
        {/* Multi-layered gradient for premium depth */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/95 via-[#0a0a0a]/60 to-[#0a0a0a]/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/40 via-transparent to-[#0a0a0a]/40" />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full px-5 md:px-10 lg:px-16 pb-10 md:pb-16 lg:pb-20 pt-32 md:pt-40">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8 lg:gap-12 items-end">
            {/* Left: Hero Content */}
            <div className={`transition-all duration-700 ${isVisible && isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              {/* Badges */}
              <div className="flex flex-wrap items-center gap-2.5 mb-5">
                <span className="px-3.5 py-1.5 bg-[#d4853a] text-white text-[11px] font-bold uppercase tracking-[0.15em] rounded-full">
                  {project.category}
                </span>
                <span className="px-3.5 py-1.5 bg-white/[0.08] backdrop-blur-md text-white/90 text-[11px] font-semibold uppercase tracking-[0.1em] rounded-full border border-white/[0.08]">
                  {project.status}
                </span>
                <span className="flex items-center gap-1.5 px-3.5 py-1.5 bg-white/[0.08] backdrop-blur-md text-white/70 text-[11px] font-medium rounded-full border border-white/[0.08]">
                  <i className="ri-map-pin-line text-[12px] text-[#d4853a]" />
                  {project.location}
                </span>
              </div>

              {/* Title */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-[3.25rem] font-extrabold text-white leading-[1.1] mb-4 max-w-3xl">
                {project.title}
              </h1>

              {/* Description */}
              <p className="text-[15px] md:text-base text-white/60 leading-relaxed max-w-2xl mb-7">
                {project.description}
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-wrap items-center gap-3">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2.5 px-7 py-3.5 bg-[#d4853a] text-white text-[13px] font-bold rounded-xl transition-all duration-300 hover:bg-[#c27630] hover:shadow-[0_12px_40px_rgba(212,133,58,0.4)] hover:-translate-y-0.5 whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-[18px]" />
                  WhatsApp Inquiry
                </a>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2 px-6 py-3.5 bg-white/[0.08] backdrop-blur-md text-white text-[13px] font-semibold rounded-xl border border-white/[0.1] transition-all duration-300 hover:bg-white/[0.14] hover:border-white/[0.2] hover:-translate-y-0.5 whitespace-nowrap"
                >
                  <i className="ri-car-line text-[16px]" />
                  Book Site Visit
                </a>
                <button className="group inline-flex items-center gap-2 px-6 py-3.5 bg-white/[0.05] text-white/70 text-[13px] font-medium rounded-xl border border-white/[0.08] transition-all duration-300 hover:bg-white/[0.1] hover:text-white/90 hover:-translate-y-0.5 whitespace-nowrap">
                  <i className="ri-download-line text-[15px]" />
                  Download Brochure
                </button>
              </div>
            </div>

            {/* Right: Floating Glass Info Card */}
            <div
              className={`hidden lg:block transition-all duration-700 delay-300 ${isVisible && isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
            >
              <div className="bg-white/[0.06] backdrop-blur-2xl rounded-2xl border border-white/[0.1] p-6 shadow-[0_20px_60px_rgba(0,0,0,0.4)]">
                <div className="text-[11px] font-bold text-white/30 uppercase tracking-[0.2em] mb-4">
                  Investment Highlights
                </div>

                {/* Plot Sizes */}
                <div className="mb-5">
                  <div className="text-[12px] text-white/50 mb-2.5">Available Plot Sizes</div>
                  <div className="flex flex-wrap gap-2">
                    {project.plotSizes.map((size) => (
                      <span
                        key={size}
                        className="px-3 py-1.5 bg-white/[0.08] text-white/80 text-[12px] font-semibold rounded-lg border border-white/[0.08]"
                      >
                        {size}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Price */}
                <div className="mb-5 pb-5 border-b border-white/[0.08]">
                  <div className="text-[12px] text-white/50 mb-1">Starting From</div>
                  <div className="text-2xl font-extrabold text-[#d4853a]">{project.priceFrom}</div>
                  <div className="text-[11px] text-white/40 mt-1">Cash Payment &bull; Possession Ready</div>
                </div>

                {/* Trust badges */}
                <div className="grid grid-cols-2 gap-2.5 mb-5">
                  <div className="flex items-center gap-2 text-white/50 text-[11px]">
                    <i className="ri-shield-check-line text-[#d4853a] text-[14px]" />
                    NOC Approved
                  </div>
                  <div className="flex items-center gap-2 text-white/50 text-[11px]">
                    <i className="ri-verified-badge-line text-[#d4853a] text-[14px]" />
                    Verified Project
                  </div>
                  <div className="flex items-center gap-2 text-white/50 text-[11px]">
                    <i className="ri-global-line text-[#d4853a] text-[14px]" />
                    Overseas Friendly
                  </div>
                  <div className="flex items-center gap-2 text-white/50 text-[11px]">
                    <i className="ri-bar-chart-box-line text-[#d4853a] text-[14px]" />
                    High ROI Potential
                  </div>
                </div>

                {/* Mini CTA */}
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-3 bg-[#d4853a] text-white text-[13px] font-bold rounded-xl transition-all duration-300 hover:bg-[#c27630] hover:shadow-lg"
                >
                  <i className="ri-whatsapp-line text-[16px]" />
                  Get Pricing Details
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom gradient fade into next section */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#FAFAF8] to-transparent z-[5]" />
    </section>
  );
}